/**
 * Settings Page
 */

import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import {
  getUserProfile,
  saveUserProfile,
  updateNotificationPreferences,
} from '../services/userProfileService';
import PaymentGatewaySettings from '../components/PaymentGatewaySettings';
import BrandingSettings from '../components/BrandingSettings';
import PortalAutomationSettings from '../components/PortalAutomationSettings';
import StripeSettings from '../components/StripeSettings';
import './Settings.css';

const Settings = () => {
  const user = useAuthStore((state) => state.user);
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Profile state - start with defaults, update if data loads
  const [profileData, setProfileData] = useState({
    businessName: '',
    businessAddress: '',
    defaultRate: 0,
    defaultCurrency: 'USD',
    taxRate: 0,
  });

  // Notification state
  const [notifications, setNotifications] = useState({
    invoicePaid: true,
    invoiceOverdue: true,
    autoReminders: true,
    weeklySummary: false,
    reminderFrequency: 7,
  });


  // Load user profile on mount (silently in background)
  useEffect(() => {
    if (user?.uid) {
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      const profile = await getUserProfile(user.uid);

      setProfileData({
        businessName: profile.businessName || '',
        businessAddress: profile.businessAddress || '',
        defaultRate: profile.defaultRate || 0,
        defaultCurrency: profile.defaultCurrency || 'USD',
        taxRate: profile.taxRate || 0,
      });

      if (profile.notifications) {
        setNotifications(profile.notifications);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleProfileChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNotificationChange = (field, value) => {
    setNotifications((prev) => ({
      ...prev,
      [field]: value,
    }));
  };


  const saveProfile = async () => {
    try {
      setSaving(true);
      await saveUserProfile(user.uid, profileData);
      showMessage('success', 'Profile settings saved successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      showMessage('error', 'Failed to save profile settings');
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async () => {
    try {
      setSaving(true);
      await updateNotificationPreferences(user.uid, notifications);
      showMessage('success', 'Notification preferences saved successfully!');
    } catch (error) {
      console.error('Error saving notifications:', error);
      showMessage('error', 'Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };


  return (
    <div>
      <h1>Settings</h1>
      <p className="page-subtitle">
        Configure your billing preferences and account settings
      </p>

      {message.text && (
        <div className={`alert alert-${message.type}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      <div className="settings-container">
          <div className="settings-tabs">
            <button
              className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              👤 Profile
            </button>
            <button
              className={`tab-button ${activeTab === 'pricing' ? 'active' : ''}`}
              onClick={() => setActiveTab('pricing')}
            >
              💰 Pricing
            </button>
            <button
              className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
              onClick={() => setActiveTab('payments')}
            >
              💳 Payments
            </button>
            <button
              className={`tab-button ${activeTab === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('notifications')}
            >
              🔔 Notifications
            </button>
            <button
              className={`tab-button ${activeTab === 'branding' ? 'active' : ''}`}
              onClick={() => setActiveTab('branding')}
            >
              🎨 Branding
            </button>
            <button
              className={`tab-button ${activeTab === 'portal' ? 'active' : ''}`}
              onClick={() => setActiveTab('portal')}
            >
              🔗 Portal Automation
            </button>
            <button
              className={`tab-button ${activeTab === 'stripe' ? 'active' : ''}`}
              onClick={() => setActiveTab('stripe')}
            >
              💳 Stripe Mode
            </button>
          </div>

          <div className="settings-content">
            {activeTab === 'profile' && (
              <div className="card">
                <h2>Profile Settings</h2>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="input"
                    value={user?.email || ''}
                    disabled
                  />
                  <p className="help-text">Your email address is managed by Firebase Authentication</p>
                </div>

                <div className="form-group">
                  <label>Business Name</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="Your Business Name"
                    value={profileData.businessName}
                    onChange={(e) => handleProfileChange('businessName', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>Business Address</label>
                  <textarea
                    className="input"
                    rows="3"
                    placeholder="Your business address for invoices"
                    value={profileData.businessAddress}
                    onChange={(e) => handleProfileChange('businessAddress', e.target.value)}
                  />
                </div>

                <button 
                  className="btn btn-primary" 
                  onClick={saveProfile}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}

            {activeTab === 'pricing' && (
              <div className="card">
                <h2>Default Pricing</h2>
                <p className="help-text">
                  Set default rates that will be used when creating new clients
                </p>

                <div className="form-group">
                  <label>Default Hourly Rate</label>
                  <input
                    type="number"
                    className="input"
                    placeholder="0.00"
                    step="0.01"
                    value={profileData.defaultRate}
                    onChange={(e) => handleProfileChange('defaultRate', parseFloat(e.target.value) || 0)}
                  />
                </div>

                <div className="form-group">
                  <label>Default Currency</label>
                  <select 
                    className="input"
                    value={profileData.defaultCurrency}
                    onChange={(e) => handleProfileChange('defaultCurrency', e.target.value)}
                  >
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="PHP">PHP - Philippine Peso</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Tax Rate (%)</label>
                  <input
                    type="number"
                    className="input"
                    placeholder="0.00"
                    step="0.01"
                    value={profileData.taxRate}
                    onChange={(e) => handleProfileChange('taxRate', parseFloat(e.target.value) || 0)}
                  />
                </div>

                <button 
                  className="btn btn-primary"
                  onClick={saveProfile}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}

            {activeTab === 'payments' && (
              <PaymentGatewaySettings />
            )}

            {activeTab === 'notifications' && (
              <div className="card">
                <h2>Notification Preferences</h2>

                <div className="notification-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={notifications.invoicePaid}
                      onChange={(e) => handleNotificationChange('invoicePaid', e.target.checked)}
                    />
                    <span>Email me when an invoice is paid</span>
                  </label>
                </div>

                <div className="notification-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={notifications.invoiceOverdue}
                      onChange={(e) => handleNotificationChange('invoiceOverdue', e.target.checked)}
                    />
                    <span>Email me when an invoice is overdue</span>
                  </label>
                </div>

                <div className="notification-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={notifications.autoReminders}
                      onChange={(e) => handleNotificationChange('autoReminders', e.target.checked)}
                    />
                    <span>Send automatic payment reminders</span>
                  </label>
                </div>

                <div className="notification-option">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={notifications.weeklySummary}
                      onChange={(e) => handleNotificationChange('weeklySummary', e.target.checked)}
                    />
                    <span>Weekly billing summary email</span>
                  </label>
                </div>

                <div className="form-group" style={{ marginTop: '2rem' }}>
                  <label>Reminder Frequency</label>
                  <select
                    className="input"
                    value={notifications.reminderFrequency}
                    onChange={(e) => handleNotificationChange('reminderFrequency', parseInt(e.target.value))}
                  >
                    <option value="3">Every 3 days</option>
                    <option value="7">Every 7 days</option>
                    <option value="14">Every 14 days</option>
                  </select>
                </div>

                <button
                  className="btn btn-primary"
                  onClick={saveNotifications}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Preferences'}
                </button>
              </div>
            )}

            {activeTab === 'branding' && (
              <BrandingSettings />
            )}

            {activeTab === 'portal' && (
              <PortalAutomationSettings />
            )}

            {activeTab === 'stripe' && (
              <StripeSettings />
            )}
          </div>
        </div>
    </div>
  );
};

export default Settings;
